/**
 * One TRAINING column of the sequence canvas: the card box, and the workout inside it.
 *
 * The twin of `DayColumn`, and the mock's own `s.cardSt` — which is the week programator's `cardSt`
 * minus the branches a cycle column cannot take. A cycle column has no empty state: a position in a
 * cycle exists because a training stands there, where a weekday exists whether or not one does. So
 * there is no `+` layer to cross-fade with, and this box holds exactly one thing.
 *
 * The column HEAD is not here. It belongs to the row — `TapePane` draws it over a training, over a
 * gap and (empty) over each tail tile, which is the mock's own structure and what keeps the three
 * kinds of column aligned along one baseline.
 *
 * ── The box is the drop target AND the drag source ───────────────────────────────────────────
 * The mock puts `draggable` on `cardSt` and `onDrop` on the column, and one element can be both. The
 * ref is created here and handed to `SessionCard`, which is still what calls `useDraggableItem` —
 * the payload is the DAY's and the menu that starts the keyboard path is the card's own.
 *
 * A position in a cycle is a SLOT of a list, not a gap between siblings: dropping a training on
 * position 2 puts it there and everything from there shifts down. So the whole column is the target,
 * there is no closest-edge hitbox and no insertion line, and a refused position never lights up —
 * `canDrop` returns false, the adapter does not enter the element, and no indicator paints
 * (view ADR §11).
 */

import { useRef } from 'react';

import { previewMoveCommand } from '~/entities/plan-draft';
import { useDropTarget } from '~/shared/ui/dnd/useDropTarget';

import type { DayCardView } from '../model/dayCardView';

import { SessionCard, type DayCardActions, type SessionMoveProps } from './SessionCard';
import styles from './CycleColumn.module.css';

interface CycleColumnProps {
  readonly day: DayCardView;
  /** This training's position in the cycle — the coordinate a drop reports. 0-based. */
  readonly index: number;
  readonly onRenameSession: ((slotToken: string, name: string) => void) | null;
  readonly onProgramDay: ((slotToken: string) => void) | null;
  readonly returnFocusToken: string | null;
  readonly move: SessionMoveProps | null;
  readonly actions: DayCardActions | null;
  /** True when a KEYBOARD lift somewhere in the cycle is currently pointed at this position. */
  readonly isPendingTarget: boolean;
  /** True when the day panel is open ON THIS day — the accent edge and the 3px ember ring. */
  readonly isSelected: boolean;
}

export function CycleColumn({
  day,
  index,
  onRenameSession,
  onProgramDay,
  returnFocusToken,
  move,
  actions,
  isPendingTarget,
  isSelected,
}: CycleColumnProps) {
  const boxRef = useRef<HTMLDivElement>(null);
  const drop = useDropTarget({
    ref: boxRef,
    data: { kind: 'tapePosition', index },
    // The entity's own gate, asked HERE so a refused position never lights up. The reason lives on
    // the card's move menu, whose "Przestaw" and stops stay focusable.
    //
    // `unchanged` is refused too, and that is deliberate: releasing a card back on its own column
    // does nothing, and the menu already says so in words ("Ten trening już tu stoi."). Accepting it
    // here made the source column light up as a destination and let the keyboard commit a no-op, so
    // one gesture was answered three different ways. The WEEK takes the opposite decision on purpose
    // — see `DayColumn`, where a weekday is a fixed slot and a dead zone around the card the trainer
    // just picked up is worse than a legal drop that does nothing. Here a position is a slot of a
    // LIST and its neighbours are the destinations, so the refusal costs nothing.
    canDrop: (source) => {
      if (move === null || source.kind !== 'session') return false;
      const daySlotToken = source.daySlotToken;
      if (typeof daySlotToken !== 'string') return false;
      const outcome = previewMoveCommand({
        kind: 'moveDayToPosition',
        daySlotToken,
        targetIndex: index,
      });
      return outcome.kind !== 'refused' && outcome.kind !== 'unchanged';
    },
  });

  return (
    <div
      ref={boxRef}
      className={styles.card}
      data-target={drop.isOver || isPendingTarget ? '' : undefined}
      data-selected={isSelected ? '' : undefined}
    >
      <SessionCard
        box={boxRef}
        day={day}
        onRename={onRenameSession}
        onProgram={onProgramDay}
        shouldTakeFocus={returnFocusToken === day.slotToken}
        move={move}
        actions={actions}
      />
    </div>
  );
}
